import React, {memo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C81_PAGE, CChecklistRow} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C81RiskAssessmentViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const LEVEL_OPTIONS = ['', 'H', 'M', 'L', 'N/A'] as const;

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.7rem',
    py: 0.75,
    px: 0.4,
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
    whiteSpace: 'pre-line' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.4,
    px: 0.4,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

const CellInput = memo(function CellInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center';
}) {
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
});

const LevelSelect = memo(function LevelSelect({
    value,
    onChange,
}: {
    value: string;
    onChange: (v: string) => void;
}) {
    const known = LEVEL_OPTIONS.includes(value as (typeof LEVEL_OPTIONS)[number]);
    return (
        <Box
            component="select"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
            sx={{
                ...nativeBaseSx,
                textAlign: 'center',
                cursor: 'pointer',
                pr: 2.5,
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: (theme: Theme) =>
                    `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                        `<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${theme.palette.text.secondary}' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>`,
                    )}")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 6px center',
            }}
        >
            {LEVEL_OPTIONS.map((opt) => (
                <option key={opt || '_'} value={opt}>
                    {opt || '—'}
                </option>
            ))}
            {!known && value ? <option value={value}>{value}</option> : null}
        </Box>
    );
});

function fieldValue(values: Record<string, string>, id: string, key: 'specific' | 'level' | 'managed') {
    const direct = values[`${id}.${key}`];
    if (direct != null && String(direct).trim() !== '') return direct;

    // migrate old checklist storage: comments (+ optional " | managed"), wpRef = H/M/L
    const comments = values[`${id}.comments`] ?? '';
    const wpRef = values[`${id}.wpRef`] ?? '';
    if (key === 'level') {
        if (['H', 'M', 'L', 'N/A', 'NA'].includes(wpRef)) {
            return wpRef === 'NA' ? 'N/A' : wpRef;
        }
        return '';
    }
    if (!comments) return '';
    const sep = ' | ';
    const idx = comments.indexOf(sep);
    if (idx >= 0) {
        if (key === 'specific') return comments.slice(0, idx);
        if (key === 'managed') return comments.slice(idx + sep.length);
    }
    return key === 'specific' ? comments : '';
}

const SectionRow = memo(function SectionRow({text}: {text: string}) {
    return (
        <TableRow>
            <TableCell sx={bodySx} colSpan={5}>
                <Typography variant="body2" fontWeight={700} sx={{py: 0.25}}>
                    {text}
                </Typography>
            </TableCell>
        </TableRow>
    );
});

const DataRow = memo(function DataRow({
    row,
    specific,
    level,
    managed,
    onChange,
}: {
    row: CChecklistRow;
    specific: string;
    level: string;
    managed: string;
    onChange: (key: string, value: string) => void;
}) {
    const indent = row.kind === 'sub';
    const onSpecific = useCallback((v: string) => onChange(`${row.id}.specific`, v), [onChange, row.id]);
    const onLevel = useCallback((v: string) => onChange(`${row.id}.level`, v), [onChange, row.id]);
    const onManaged = useCallback((v: string) => onChange(`${row.id}.managed`, v), [onChange, row.id]);

    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography variant="body2" fontWeight={600} sx={{pl: indent ? 1 : 0}}>
                    {row.num}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <Typography variant="body2" sx={{lineHeight: 1.4, pl: indent ? 1.5 : 0}}>
                    {row.text}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={specific} onChange={onSpecific}/>
            </TableCell>
            <TableCell sx={bodySx}>
                <LevelSelect value={level} onChange={onLevel}/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={managed} onChange={onManaged}/>
            </TableCell>
        </TableRow>
    );
});

/** Excel C8.1 — Detailed Risk Assessment */
export default function C81RiskAssessmentView({header, values, onChange}: C81RiskAssessmentViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stableOnChange = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C8.1"
                values={values}
                onChange={stableOnChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1, mt: 0.5}}
            >
                {C81_PAGE.title}
            </Typography>

            {C81_PAGE.intros.map((intro, idx) => (
                <Typography
                    key={idx}
                    variant="body2"
                    sx={{mb: 1.25, lineHeight: 1.45, whiteSpace: 'pre-line'}}
                >
                    {intro}
                </Typography>
            ))}

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 1100,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '5%'}}/>
                        <col style={{width: '38%'}}/>
                        <col style={{width: '22%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '25%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2} align="center">
                                {C81_PAGE.columns.area}
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                {C81_PAGE.columns.specific}
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                {C81_PAGE.columns.level}
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                {C81_PAGE.columns.managed}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C81_PAGE.rows.map((row) => {
                            if (row.kind === 'section') {
                                return <SectionRow key={row.id} text={row.text}/>;
                            }
                            return (
                                <DataRow
                                    key={row.id}
                                    row={row}
                                    specific={fieldValue(values, row.id, 'specific')}
                                    level={fieldValue(values, row.id, 'level')}
                                    managed={fieldValue(values, row.id, 'managed')}
                                    onChange={stableOnChange}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
